import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  CardContent,
  IconButton,
  Modal,
  Grow,
  useTheme,
  useMediaQuery,
  alpha,
  styled,
  TextField,
  Container,
  Grid,
  Card,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import PaymentIcon from "@mui/icons-material/Payment";
import CodeIcon from "@mui/icons-material/Code";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import ArticleIcon from "@mui/icons-material/Article";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import logo from "./assets/logo.png";
import slider1 from "./assets/ip7.jpg";
import Footer from "./Footr";
import NotificationSnackbar from "./NotificationBar";

const icons = [
  <HelpOutlineIcon sx={{ fontSize: 28 }} />,
  <DescriptionIcon sx={{ fontSize: 28 }} />,
  <PaymentIcon sx={{ fontSize: 28 }} />,
  <CodeIcon sx={{ fontSize: 28 }} />,
  <SettingsIcon sx={{ fontSize: 28 }} />,
  <PeopleIcon sx={{ fontSize: 28 }} />,
];
const getIconByIndex = (index) => icons[index % icons.length];
const cardColors = [
  {
    main: "#3b82f6",
    bg: "#eff6ff",
    gradient: "linear-gradient(135deg, #3b82f6, #2563eb)",
  },
  {
    main: "#10b981",
    bg: "#ecfdf5",
    gradient: "linear-gradient(135deg, #10b981, #059669)",
  },
  {
    main: "#06b6d4",
    bg: "#ecfeff",
    gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
  },
];

const HeroBox = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "calc(100vh - 48px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.common.white,
  overflow: "hidden",
  paddingTop: "60px",
  [theme.breakpoints.down("sm")]: {
    minHeight: "calc(100vh - 32px)",
  },
}));

const MediaBackground = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  zIndex: 0,
});

const Overlay = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background:
    "linear-gradient(135deg, rgba(10, 46, 82, 0.92) 0%, rgba(21, 101, 192, 0.75) 100%)",
  zIndex: 0,
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.common.white,
    borderRadius: "16px",
    paddingLeft: theme.spacing(1),
    fontSize: "1.1rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
    transition: "all 0.3s ease",
    "& fieldset": { border: "none" },
    "&:hover": {
      boxShadow: "0 12px 50px rgba(0, 0, 0, 0.25)",
      transform: "translateY(-1px)",
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const controls = useAnimation();
  const searchResultsRef = useRef(null);

  // State
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [knowledgeSections, setKnowledgeSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  // Login State
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchResults, setSearchResults] = useState({
    categories: [],
    faqs: [],
    articles: [],
  });
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const categories = useSelector((state) => state.support.categories) || [];
  const filteredArticles =
    useSelector((state) => state.support.filteredArticles) || [];
  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleLoginClick = () => setIsLoginOpen(true);
  const handleCloseLogin = () => setIsLoginOpen(false);
  const handleTogglePassword = () => setShowPassword(!showPassword);

  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight - 48,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const filteredData = categories.filter(
      (item) => item.isOnHomePage === true,
    );
    const transformedSections = filteredData.map((item, index) => {
      const fullDescription = item.descriptions || "No description provided";
      const shortDescription =
        fullDescription.length > 115
          ? fullDescription.substring(0, 115) + "..."
          : fullDescription;
      return {
        id: item.id,
        title: item.categoryName || "Untitled",
        descriptions: shortDescription,
        icon: getIconByIndex(index),
      };
    });
    setKnowledgeSections(transformedSections);
    setLoading(false);
  }, [categories]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setUsernameError(false);
    setPasswordError(false);

    let valid = true;
    if (!username.trim()) {
      setUsernameError(true);
      valid = false;
    }
    if (!password.trim()) {
      setPasswordError(true);
      valid = false;
    }
    if (!valid) return;

    setLoginLoading(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: username, password }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Login failed");

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userToken", data.token);
      localStorage.setItem("userData", JSON.stringify(data.user));
      handleSnackbarOpen("Login successful!", "success");

      const role = data.user.roleId?.toUpperCase();
      setTimeout(() => {
        switch (role) {
          case "SUPPORT_AGENT":
            navigate("/AssignedTickets");
            break;
          case "APPLICANT":
          case "EMPLOYEE":
          case "NO_ROLE":
            navigate("/RespondedTickets");
            break;
          default:
            navigate("/PendingTickets");
        }
      }, 1000);
    } catch (error) {
      console.error("Login error:", error);
      handleSnackbarOpen(error?.message, "error");
    } finally {
      setLoginLoading(false);
    }
  };

  // Search Logic
  const highlightMatch = (text, term) => {
    if (!term || !text) return text;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");

    return text
      .toString()
      .split(regex)
      .map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              backgroundColor: "#fef08a",
              color: "#1e293b",
              padding: "0 2px",
              borderRadius: "4px",
              fontWeight: 600,
            }}
          >
            {part}
          </mark>
        ) : (
          part
        ),
      );
  };

  const performSearch = async (term) => {
    if (!term.trim()) {
      setSearchResults({ categories: [], faqs: [], articles: [] });
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    const lowerTerm = term.toLowerCase().trim();

    await controls.start({ opacity: 0, y: 20, transition: { duration: 0.2 } });

    try {
      const matchedCategories = categories.filter(
        (category) =>
          category.categoryName.toLowerCase().includes(lowerTerm) ||
          category.descriptions.toLowerCase().includes(lowerTerm),
      );

      const faqsRes = await axios.get("/api/support/faqs");
      const matchedFaqs = (faqsRes.data.filteredFaqs || []).filter((faq) => {
        return (
          (faq.title && faq.title.toLowerCase().includes(lowerTerm)) ||
          (faq.categoryName &&
            faq.categoryName.toLowerCase().includes(lowerTerm)) ||
          (faq.createdBy && faq.createdBy.toLowerCase().includes(lowerTerm)) ||
          (faq.content && faq.content.toLowerCase().includes(lowerTerm))
        );
      });

      const matchedArticles = filteredArticles.filter((article) => {
        return (
          (article.title && article.title.toLowerCase().includes(lowerTerm)) ||
          (article.categoryName &&
            article.categoryName.toLowerCase().includes(lowerTerm)) ||
          (article.createdBy &&
            article.createdBy.toLowerCase().includes(lowerTerm)) ||
          (article.content && article.content.toLowerCase().includes(lowerTerm))
        );
      });

      setSearchResults({
        categories: matchedCategories,
        faqs: matchedFaqs,
        articles: matchedArticles,
      });
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults({ categories: [], faqs: [], articles: [] });
    } finally {
      setIsSearching(false);
      setTimeout(() => {
        searchResultsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        controls.start({
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
        });
      }, 100);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => performSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <Box
      sx={{ overflowX: "hidden", fontFamily: "'Inter', 'Roboto', sans-serif" }}
    >
      {/* ---------- NAVIGATION BAR ---------- */}
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          background: scrolled ? "rgba(10, 46, 82, 0.9)" : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.1)" : "none",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          py: scrolled ? 0.5 : 1,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 4 } }}>
          <Box
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="logo"
              sx={{
                height: 36,
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.5,
                color: "white",
                display: { xs: "none", md: "block" },
              }}
            >
              IPPIS SUPPORT
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<PersonIcon />}
              onClick={handleLoginClick}
              sx={{
                borderRadius: "8px",
                px: { xs: 2, sm: 3 },
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "white",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                  boxShadow: "none",
                },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                Login
              </Box>
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SupportAgentIcon />}
              onClick={() => navigate("/CreateTicket")}
              sx={{
                borderRadius: "8px",
                px: { xs: 2, sm: 3 },
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(90deg, #1d4ed8, #2563eb)",
                boxShadow: "0 4px 14px rgba(37, 99, 235, 0.4)",
                "&:hover": {
                  background: "linear-gradient(90deg, #1e40af, #1d4ed8)",
                },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                Get Support
              </Box>
            </Button>
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={() => navigate("/recruiting-company-welcome-page")}
              sx={{
                borderRadius: "8px",
                px: { xs: 2, sm: 3 },
                textTransform: "none",
                fontWeight: 600,
                color: "white",
                borderColor: "rgba(255,255,255,0.5)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", md: "block" } }}
              >
                For Recruiters
              </Box>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ---------- HERO SECTION ---------- */}
      <HeroBox>
        <MediaBackground component="img" src={slider1} alt="Background" />
        <Overlay />

        <Container
          maxWidth="md"
          sx={{ position: "relative", zIndex: 1, textAlign: "center" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              sx={{
                fontWeight: 800,
                mb: 2,
                background: "linear-gradient(to right, #ffffff, #e2e8f0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 4px 20px rgba(0,0,0,0.2)",
              }}
            >
              How can we help you today?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 5,
                color: "rgba(255,255,255,0.85)",
                fontWeight: 400,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Search our knowledge base to find answers, setup guides, and
              troubleshooting steps for IPPIS.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Box sx={{ maxWidth: "700px", mx: "auto", position: "relative" }}>
              <SearchField
                fullWidth
                placeholder="Search for articles, modules, or FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: "#64748b", fontSize: 28, ml: 1 }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchTerm("")}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)", mt: 1 }}
                >
                  Popular:
                </Typography>
                {["Recruitment", "Payroll", "RBM", "Account"].map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onClick={() => setSearchTerm(tag)}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.1)",
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </motion.div>
        </Container>

        {/* Scroll Indicator */}
        <Box
          onClick={handleScrollDown}
          sx={{
            position: "absolute",
            bottom: "40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            color: "rgba(255,255,255,0.6)",
            transition: "color 0.3s ease",
            "&:hover": { color: "white" },
            zIndex: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ letterSpacing: 2, textTransform: "uppercase", mb: 1 }}
          >
            Explore
          </Typography>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderBottom: "2px solid",
                borderRight: "2px solid",
                transform: "rotate(45deg)",
              }}
            />
          </motion.div>
        </Box>
      </HeroBox>

      {/* ---------- SEARCH RESULTS ---------- */}
      <div ref={searchResultsRef}>
        <AnimatePresence>
          {hasSearched && (
            <Box
              component={motion.div}
              animate={controls}
              sx={{ py: 8, backgroundColor: "#f8fafc", minHeight: "60vh" }}
            >
              <Container maxWidth="lg">
                <Box
                  sx={{
                    mb: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#0f172a" }}
                  >
                    Search Results
                  </Typography>
                  <Chip
                    label={`${searchResults.categories.length + searchResults.faqs.length + searchResults.articles.length} results`}
                    color="primary"
                    sx={{ fontWeight: 600, borderRadius: "8px" }}
                  />
                </Box>

                {isSearching ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 10 }}
                  >
                    <CircularProgress size={48} sx={{ color: "#3b82f6" }} />
                  </Box>
                ) : (
                  <Grid container spacing={4}>
                    {/* Articles Section */}
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #e2e8f0",
                          boxShadow: "none",
                          height: "100%",
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 3,
                            }}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: "12px",
                                backgroundColor: "#eff6ff",
                                color: "#3b82f6",
                                mr: 2,
                              }}
                            >
                              <ArticleIcon />
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, flexGrow: 1 }}
                            >
                              Articles
                            </Typography>
                            <Chip
                              size="small"
                              label={searchResults.articles.length}
                              sx={{ backgroundColor: "#f1f5f9" }}
                            />
                          </Box>
                          <Divider sx={{ mb: 2 }} />

                          {searchResults.articles.length > 0 ? (
                            <Box
                              sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}
                            >
                              {searchResults.articles.map((article) => (
                                <Box
                                  key={article.id}
                                  onClick={() =>
                                    navigate(`/articles/${article.id}`)
                                  }
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                    border: "1px solid transparent",
                                    "&:hover": {
                                      borderColor: "#cbd5e1",
                                      backgroundColor: "#f8fafc",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#1e293b",
                                      mb: 1,
                                    }}
                                  >
                                    {highlightMatch(article.title, searchTerm)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#64748b" }}
                                  >
                                    Category:{" "}
                                    {highlightMatch(
                                      article.categoryName,
                                      searchTerm,
                                    )}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="center"
                              sx={{ py: 4 }}
                            >
                              No articles found.
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* FAQs Section */}
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #e2e8f0",
                          boxShadow: "none",
                          height: "100%",
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 3,
                            }}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: "12px",
                                backgroundColor: "#f0fdf4",
                                color: "#16a34a",
                                mr: 2,
                              }}
                            >
                              <QuestionAnswerIcon />
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, flexGrow: 1 }}
                            >
                              FAQs
                            </Typography>
                            <Chip
                              size="small"
                              label={searchResults.faqs.length}
                              sx={{ backgroundColor: "#f1f5f9" }}
                            />
                          </Box>
                          <Divider sx={{ mb: 2 }} />

                          {searchResults.faqs.length > 0 ? (
                            <Box
                              sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}
                            >
                              {searchResults.faqs.map((faq) => (
                                <Box
                                  key={faq.id}
                                  onClick={() => navigate(`/faqs/${faq.id}`)}
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                    border: "1px solid transparent",
                                    "&:hover": {
                                      borderColor: "#cbd5e1",
                                      backgroundColor: "#f8fafc",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#1e293b",
                                      mb: 1,
                                    }}
                                  >
                                    {highlightMatch(faq.title, searchTerm)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#64748b" }}
                                  >
                                    Category:{" "}
                                    {highlightMatch(
                                      faq.categoryName,
                                      searchTerm,
                                    )}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="center"
                              sx={{ py: 4 }}
                            >
                              No FAQs found.
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </Container>
            </Box>
          )}
        </AnimatePresence>
      </div>

      {/* ---------- KNOWLEDGE BASE (CATEGORIES) ---------- */}
      {!hasSearched && (
        <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: "#f8fafc" }}>
          <Container maxWidth="lg">
            <Box textAlign="center" mb={8}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  letterSpacing: "-0.02em",
                  background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline-block",
                }}
              >
                Browse by IPPIS Module
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#64748b",
                  fontWeight: 400,
                  maxWidth: "600px",
                  mx: "auto",
                  lineHeight: 1.6,
                }}
              >
                Select a module below to find detailed guides, articles, and
                frequently asked questions.
              </Typography>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress size={48} sx={{ color: "#3b82f6" }} />
              </Box>
            ) : (
              <Grid container spacing={4}>
                {knowledgeSections.map((section, index) => {
                  const colorTheme = cardColors[index % cardColors.length];

                  return (
                    <Grid item xs={12} sm={6} md={4} key={section.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{
                          delay: index * 0.1,
                          duration: 0.6,
                          ease: "easeOut",
                        }}
                        style={{ height: "100%" }}
                      >
                        <Card
                          onClick={() =>
                            navigate(
                              `/ArticlesPage?id=${encodeURIComponent(section.id)}`,
                            )
                          }
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: "24px",
                            border: "1px solid #f1f5f9",
                            backgroundColor: "#ffffff",
                            boxShadow: "0 10px 40px -10px rgba(0,0,0,0.03)",
                            position: "relative",
                            overflow: "hidden",
                            cursor: "pointer",
                            transition:
                              "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                            "&:hover": {
                              transform: "translateY(-8px)",
                              boxShadow: `0 20px 40px -10px ${alpha(colorTheme.main, 0.2)}`,
                              borderColor: alpha(colorTheme.main, 0.3),
                            },
                            "&:hover .action-btn": {
                              backgroundColor: colorTheme.main,
                              color: "#fff",
                            },
                            "&:hover .action-arrow": {
                              transform: "translateX(6px)",
                            },
                          }}
                        >
                          {/* Soft background glow element */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: -30,
                              right: -30,
                              width: 150,
                              height: 150,
                              borderRadius: "50%",
                              background: `radial-gradient(circle, ${alpha(colorTheme.main, 0.1)} 0%, transparent 70%)`,
                              zIndex: 0,
                            }}
                          />

                          <CardContent
                            sx={{
                              flexGrow: 1,
                              p: 4,
                              pb: 3,
                              position: "relative",
                              zIndex: 1,
                            }}
                          >
                            {/* Flex container holding Icon and Title together */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 52,
                                  height: 52,
                                  borderRadius: "16px",
                                  background: colorTheme.gradient,
                                  color: "#ffffff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: 2.5,
                                  boxShadow: `0 8px 16px -4px ${alpha(colorTheme.main, 0.4)}`,
                                  flexShrink: 0,
                                }}
                              >
                                {React.cloneElement(section.icon, {
                                  sx: { fontSize: 28, color: "#fff" },
                                })}
                              </Box>

                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 800,
                                  color: colorTheme.main, // Dynamically vibrant color, not black
                                  letterSpacing: "-0.01em",
                                  lineHeight: 1.2,
                                }}
                              >
                                {section.title}
                              </Typography>
                            </Box>

                            {/* Description text */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#475569",
                                lineHeight: 1.7,
                                fontSize: "0.95rem",
                              }}
                            >
                              {section.descriptions}
                            </Typography>
                          </CardContent>

                          <Box
                            sx={{
                              px: 4,
                              pb: 4,
                              pt: 0,
                              position: "relative",
                              zIndex: 1,
                            }}
                          >
                            <Box
                              className="action-btn"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 2.5,
                                py: 1,
                                borderRadius: "12px",
                                backgroundColor: colorTheme.bg,
                                color: colorTheme.main,
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                transition: "all 0.3s ease",
                              }}
                            >
                              Browse articles
                              <ArrowForwardIcon
                                className="action-arrow"
                                sx={{
                                  ml: 1,
                                  fontSize: 18,
                                  transition: "transform 0.3s ease",
                                }}
                              />
                            </Box>
                          </Box>
                        </Card>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Container>
        </Box>
      )}

      {/* ---------- LOGIN MODAL ---------- */}
      <Modal
        open={isLoginOpen}
        onClose={handleCloseLogin}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(15, 23, 42, 0.6)",
        }}
      >
        <Grow in={isLoginOpen} timeout={400}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 420,
              bgcolor: "#ffffff",
              borderRadius: "24px",
              p: { xs: 3, md: 5 },
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              outline: "none",
              position: "relative",
              mx: 2,
            }}
          >
            <IconButton
              onClick={handleCloseLogin}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "#94a3b8",
                "&:hover": { backgroundColor: "#f1f5f9", color: "#0f172a" },
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{ height: 48, mb: 2 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: "#0f172a" }}
              >
                Welcome Back
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 1 }}>
                Log in to manage your tickets and account.
              </Typography>
            </Box>

            <form onSubmit={handleLoginSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                error={usernameError}
                helperText={usernameError ? "Email is required" : ""}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                }}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={passwordError}
                helperText={passwordError ? "Password is required" : ""}
                sx={{
                  mb: 4,
                  "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                        sx={{ color: "#94a3b8" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loginLoading}
                sx={{
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  background: "linear-gradient(to right, #2563eb, #1d4ed8)",
                  boxShadow: "0 10px 15px -3px rgba(37, 99, 235, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(to right, #1d4ed8, #1e40af)",
                  },
                }}
              >
                {loginLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </Box>
        </Grow>
      </Modal>

      {/* Footer & Notifications */}
      <Footer />
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default Home;
